import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { connectQZTray, getAvailablePrinters, isQZConnected } from "@/utils/printService";
import { X, Plus, Loader2, Check, Download, ExternalLink } from "lucide-react";

export const PrintStationsManager = ({ authAxios, restaurant, onUpdate }) => {
  const savedPrinters = restaurant?.print_stations || [];
  const [printers, setPrinters] = useState(savedPrinters);
  const [qzConnected, setQzConnected] = useState(false);
  const [qzConnecting, setQzConnecting] = useState(false);
  const [availablePrinters, setAvailablePrinters] = useState([]);
  const [saving, setSaving] = useState(false);
  const [expandedIdx, setExpandedIdx] = useState(null);

  useEffect(() => { setPrinters(restaurant?.print_stations || []); }, [restaurant]);

  const connectQZ = async () => {
    setQzConnecting(true);
    try {
      const connected = await connectQZTray();
      if (connected) {
        const found = await getAvailablePrinters();
        setAvailablePrinters(found || []);
        setQzConnected(true);
        toast.success(`QZ Tray conectado - ${(found || []).length} impressora(s) encontrada(s)`);
      } else {
        throw new Error("Connection failed");
      }
    } catch {
      setQzConnected(false);
      toast.error("Nao foi possivel conectar ao QZ Tray. Verifique se esta instalado e em execucao.");
    }
    setQzConnecting(false);
  };

  const addPrinter = () => {
    setPrinters([...printers, {
      id: Date.now().toString(), service: "qz_tray", printer_name: "", alias: "", columns: 35,
      copies: 1, template: "complete", auto_print: false, show_store_name: true, show_cif: false,
      show_order_number: true, show_order_position: true, show_order_channel: true,
      show_delivery_estimate: true, show_item_variation_prices: true, show_customer_notes: false,
      show_delivery_data: true, double_font_size: false,
    }]);
    setExpandedIdx(printers.length);
  };

  const removePrinter = (idx) => { setPrinters(prev => prev.filter((_, i) => i !== idx)); if (expandedIdx === idx) setExpandedIdx(null); };
  const updatePrinter = (idx, field, value) => setPrinters(prev => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));

  const savePrinters = async () => {
    setSaving(true);
    try { await authAxios.patch("/restaurant", { print_stations: printers }); toast.success("Impressoras salvas"); onUpdate(); }
    catch { toast.error("Erro ao salvar"); }
    setSaving(false);
  };

  const toggleItems = [
    { key: "auto_print", label: "Imprimir automaticamente" },
    { key: "show_store_name", label: "Imprimir nome da loja" },
    { key: "show_order_number", label: "Imprimir numero do pedido" },
    { key: "show_delivery_estimate", label: "Imprimir previsao de entrega" },
    { key: "show_item_variation_prices", label: "Imprimir precos das variacoes" },
    { key: "show_cif", label: "Imprimir NIF/CIF" },
    { key: "show_order_position", label: "Imprimir posicao do pedido" },
    { key: "show_order_channel", label: "Imprimir canal do pedido" },
    { key: "show_customer_notes", label: "Imprimir observacoes do cliente" },
    { key: "show_delivery_data", label: "Imprimir dados de entrega" },
    { key: "double_font_size", label: "Duplicar tamanho da fonte" },
  ];

  const templateOptions = [
    { value: "complete", label: "Completo" },
    { value: "cocina", label: "Cozinha" },
    { value: "bar", label: "Bar" },
    { value: "minimal", label: "Minimo" },
  ];

  return (
    <Card className="bg-card border-border/50">
      <CardHeader>
        <CardTitle className="uppercase">Impressoras</CardTitle>
        <CardDescription>Para imprimir pedidos, adicione impressoras. Para encontrar impressoras e imprimir no PC e necessario instalar <strong>QZ Tray</strong>. Para Android e necessario <strong>RawBT</strong>.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex items-center gap-3">
          <Button variant={qzConnected ? "outline" : "default"} onClick={connectQZ} disabled={qzConnecting} data-testid="connect-qz-btn">
            {qzConnecting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {qzConnected ? "QZ Tray Conectado" : "Conectar QZ Tray"}
          </Button>
          {qzConnected && <Badge variant="secondary" className="bg-emerald-100 text-emerald-700" data-testid="qz-status">{availablePrinters.length} impressora(s) detectada(s)</Badge>}
          {!qzConnected && !qzConnecting && <a href="https://qz.io/download/" target="_blank" rel="noopener noreferrer" className="text-xs text-primary underline">Baixar QZ Tray</a>}
        </div>

        {/* Setup Guide */}
        {!qzConnected && (
          <Card className="bg-blue-50/50 border-blue-200">
            <CardContent className="p-4 space-y-3">
              <p className="text-sm font-bold text-blue-900">Guia Rapido de Configuracao</p>
              <div className="space-y-2 text-sm text-blue-800">
                <div className="flex gap-2">
                  <Badge className="bg-blue-600 text-white shrink-0 h-5 w-5 flex items-center justify-center rounded-full p-0 text-xs">1</Badge>
                  <span><strong>Instalar Java</strong> (necessario para QZ Tray): <a href="https://www.java.com/pt-BR/download/" target="_blank" rel="noopener noreferrer" className="underline font-semibold">Baixar Java</a></span>
                </div>
                <div className="flex gap-2">
                  <Badge className="bg-blue-600 text-white shrink-0 h-5 w-5 flex items-center justify-center rounded-full p-0 text-xs">2</Badge>
                  <span><strong>Instalar QZ Tray</strong>: <a href="https://qz.io/download/" target="_blank" rel="noopener noreferrer" className="underline font-semibold">Baixar QZ Tray</a> e instalar</span>
                </div>
                <div className="flex gap-2">
                  <Badge className="bg-blue-600 text-white shrink-0 h-5 w-5 flex items-center justify-center rounded-full p-0 text-xs">3</Badge>
                  <span><strong>Iniciar QZ Tray</strong> — o icone aparece na bandeja do sistema (perto do relogio)</span>
                </div>
                <div className="flex gap-2">
                  <Badge className="bg-blue-600 text-white shrink-0 h-5 w-5 flex items-center justify-center rounded-full p-0 text-xs">4</Badge>
                  <span><strong>Clicar "Conectar QZ Tray"</strong> acima — o sistema detecta suas impressoras automaticamente</span>
                </div>
              </div>
              <p className="text-[11px] text-blue-600">A impressao silenciosa (sem popup) e automatica com o certificado do Kaiso System.</p>
            </CardContent>
          </Card>
        )}
        <Separator />

        {printers.map((printer, idx) => (
          <div key={printer.id} className={`border rounded-lg transition-all ${expandedIdx === idx ? "border-primary/50" : "border-border/50"}`} data-testid={`printer-station-${idx}`}>
            <div className="flex items-center justify-between p-4 cursor-pointer" onClick={() => setExpandedIdx(expandedIdx === idx ? null : idx)}>
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-sm font-bold text-primary flex-shrink-0">
                  {(printer.alias || printer.printer_name || "?")[0]?.toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-sm truncate">{printer.alias || printer.printer_name || "Sem configuracao"}</p>
                  <p className="text-xs text-muted-foreground truncate">{printer.printer_name || "Selecione uma impressora"} - {printer.template}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Badge variant="secondary" className="text-[10px]">{printer.service === "rawbt" ? "RawBT" : "QZ Tray"}</Badge>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={e => { e.stopPropagation(); removePrinter(idx); }} data-testid={`remove-printer-${idx}`}><X className="w-4 h-4" /></Button>
              </div>
            </div>

            {expandedIdx === idx && (
              <div className="px-4 pb-5 space-y-5 border-t border-border/30 pt-4">
                <div className="flex items-center gap-4">
                  <Label className="text-sm font-medium">Servico</Label>
                  <label className="flex items-center gap-1.5 cursor-pointer"><input type="radio" name={`service-${idx}`} checked={printer.service !== "rawbt"} onChange={() => updatePrinter(idx, "service", "qz_tray")} className="accent-primary" /><span className="text-sm">QZ Tray</span></label>
                  <label className="flex items-center gap-1.5 cursor-pointer"><input type="radio" name={`service-${idx}`} checked={printer.service === "rawbt"} onChange={() => updatePrinter(idx, "service", "rawbt")} className="accent-primary" /><span className="text-sm">RawBT</span></label>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-primary font-semibold">Nome *</Label>
                    {qzConnected && availablePrinters.length > 0 ? (
                      <Select value={printer.printer_name} onValueChange={v => updatePrinter(idx, "printer_name", v)}>
                        <SelectTrigger className="bg-secondary border-transparent text-sm" data-testid={`printer-name-${idx}`}><SelectValue placeholder="Selecionar..." /></SelectTrigger>
                        <SelectContent>{availablePrinters.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : (
                      <Input value={printer.printer_name} onChange={e => updatePrinter(idx, "printer_name", e.target.value)} placeholder="Generic / Text Only" className="bg-secondary border-transparent text-sm" data-testid={`printer-name-${idx}`} />
                    )}
                  </div>
                  <div className="space-y-1.5"><Label className="text-xs">Alias</Label><Input value={printer.alias} onChange={e => updatePrinter(idx, "alias", e.target.value)} placeholder="ex: caixa, cozinha" className="bg-secondary border-transparent text-sm" data-testid={`printer-alias-${idx}`} /></div>
                  <div className="space-y-1.5"><Label className="text-xs">Quantidade de colunas *</Label><Input type="number" value={printer.columns} onChange={e => updatePrinter(idx, "columns", parseInt(e.target.value) || 35)} className="bg-secondary border-transparent text-sm" data-testid={`printer-columns-${idx}`} /></div>
                  <div className="space-y-1.5"><Label className="text-xs">Copias</Label><Input type="number" value={printer.copies} onChange={e => updatePrinter(idx, "copies", parseInt(e.target.value) || 1)} min={1} max={5} className="bg-secondary border-transparent text-sm" data-testid={`printer-copies-${idx}`} /></div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Template</Label>
                  <Select value={printer.template} onValueChange={v => updatePrinter(idx, "template", v)}>
                    <SelectTrigger className="w-48 bg-secondary border-transparent text-sm" data-testid={`printer-template-${idx}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{templateOptions.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Separator />
                <div className="grid md:grid-cols-2 gap-x-8 gap-y-3">
                  {toggleItems.map(item => (
                    <div key={item.key} className="flex items-center justify-between py-0.5">
                      <span className="text-sm">{item.label}</span>
                      <Switch checked={printer[item.key] || false} onCheckedChange={v => updatePrinter(idx, item.key, v)} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}

        <Button variant="outline" className="w-full border-dashed" onClick={addPrinter} data-testid="add-printer-btn"><Plus className="w-4 h-4 mr-2" /> ADICIONAR IMPRESSORA</Button>
        {printers.length > 0 && (
          <Button className="glow-orange w-full" onClick={savePrinters} disabled={saving} data-testid="save-printers-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}Salvar Impressoras
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
